import { useEffect, useState, type PropsWithChildren } from "react";

import { useCampaign } from "hooks/api/use-campaigns";
import { useCurrentClientId } from "hooks/api/use-clients";
import { useEmailWriterCompletionPolling } from "hooks/use-email-writer-completion-polling";

import {
  EMAIL_WRITER_POLLING_SESSION_CHANGED,
  getEmailWriterPollingCampaignIds,
} from "utilities/email-writer-polling-session";

const EmailWriterCampaignPoller = ({
  clientId,
  campaignId,
}: {
  clientId: string;
  campaignId: string;
}) => {
  const { data: campaign } = useCampaign({ clientId, id: campaignId });

  useEmailWriterCompletionPolling({
    clientId,
    campaignId,
    campaignName: campaign?.name ?? "Campaign",
    enabled: Boolean(campaign),
  });

  return null;
};

export const ClientEmailWriterPollingProvider = ({ children }: PropsWithChildren) => {
  const clientId = useCurrentClientId();
  const [pollingCampaignIds, setPollingCampaignIds] = useState<string[]>([]);

  useEffect(() => {
    if (!clientId) {
      setPollingCampaignIds([]);
      return;
    }

    const syncSessions = () => {
      setPollingCampaignIds(getEmailWriterPollingCampaignIds(clientId));
    };

    syncSessions();

    window.addEventListener(EMAIL_WRITER_POLLING_SESSION_CHANGED, syncSessions);

    return () => {
      window.removeEventListener(EMAIL_WRITER_POLLING_SESSION_CHANGED, syncSessions);
    };
  }, [clientId]);

  return (
    <>
      {clientId &&
        pollingCampaignIds.map((campaignId) => (
          <EmailWriterCampaignPoller key={campaignId} clientId={clientId} campaignId={campaignId} />
        ))}
      {children}
    </>
  );
};
